import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { type ReactNode, useState } from 'react';

import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for deletions. */
  destructive?: boolean;
  /** The icon in the tile (default: a bin when destructive, a question mark otherwise). */
  icon?: ReactNode;
  /**
   * Runs on confirm; the dialog shows a spinner until it settles and closes when it resolves.
   * When it rejects the dialog stays open: the caller (or the global error toast) shows the error.
   */
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  icon,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false);

  const confirm = async () => {
    setPending(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // Stays open; the error is shown by whoever ran the action.
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      title={title}
      description={message}
      size="sm"
      closeButton={false}
      icon={{
        node: icon ?? (destructive ? <DeleteRoundedIcon /> : <HelpOutlineRoundedIcon />),
        tone: destructive ? 'danger' : 'neutral',
      }}
      actions={
        <>
          <Button variant="outline" tone="secondary" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            loading={pending}
            onClick={() => void confirm()}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
